import type { AssetFilters } from '~/types/filters'
import type { ReportPayload } from '~/types/report'
import type { ServiceScope } from '~/types/report-service'
import type { ReportPreviewParams, ReportPreviewRequest, ReportWindow } from '~/types/report-preview'
import { getReportPreviewPreset, type ReportWindowMode } from '~/utils/reports/preview-presets'

type WindowDates = Pick<ReportPayload, 'startDate' | 'endDate' | 'datePeriod'>

export const resolveWindowDates = (window: ReportWindow, mode: ReportWindowMode): WindowDates => {
  if ('preset' in window) return { datePeriod: window.preset }
  if ('from' in window && mode !== 'instant') return { startDate: window.from, endDate: window.to }
  return { startDate: window.to, endDate: window.to }
}

const scopeAssetFilter = (scope: ServiceScope | undefined): AssetFilters | undefined =>
  scope?.filter
    ? {
        assetTypeIds: [...scope.filter.assetTypeIds],
        ...(scope.filter.assetIds?.length ? { assetIds: [...scope.filter.assetIds] } : {}),
        ...(scope.filter.conditions ? { conditions: scope.filter.conditions } : {})
      }
    : undefined

export const toScheduleSummaryPayload = (request: ReportPreviewRequest): ReportPayload => {
  const { window, locale, scope, body } = request.params
  const scopeFilter = body.filter || body.reportFilter ? undefined : scopeAssetFilter(scope)

  return {
    ...body,
    ...(scopeFilter ? { filter: scopeFilter } : {}),
    ...resolveWindowDates(window, getReportPreviewPreset(request.reportType).windowMode),
    isoLangCode: locale
  }
}

export const isPreviewRequestReady = (request: ReportPreviewRequest | undefined): boolean => {
  if (!request) return false
  const { body, window, locale, scope } = request.params
  const hasScope = !!(scope || body.filter || body.reportFilter)
  const hasWindow = 'preset' in window ? !!window.preset : 'from' in window ? !!window.from && !!window.to : !!window.to
  return hasScope && hasWindow && !!locale
}

export const canonical = (input: unknown): unknown => {
  if (Array.isArray(input)) return input.map(canonical)
  if (input && typeof input === 'object') {
    return Object.fromEntries(
      Object.entries(input as Record<string, unknown>)
        .filter(([, entry]) => entry !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, canonical(entry)])
    )
  }
  return input
}

export const hashPreviewRequest = (request: ReportPreviewRequest | undefined): string =>
  request
    ? JSON.stringify(
        canonical({
          params: request.params,
          sort: request.sort,
          pageSize: request.pageSize
        })
      )
    : ''

export const hashPreviewParams = (params: ReportPreviewParams | undefined): string =>
  params ? JSON.stringify(canonical(params)) : ''
